import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { ClientError, gql } from 'graphql-request';
import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser';
import { gqlClient } from '@/api';

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
  /**
   * The photo this person shows, or null while they show their initial.
   *
   * Read by two screens that have to agree — the header's circle and the
   * profile card — which is why it lives on the one account object both of them
   * already read rather than being fetched twice.
   */
  avatarUrl: string | null;
}

/**
 * Why the sign-in mutation answers with a shape instead of throwing: a wrong
 * password is a normal outcome, not a server error, and the API keeps room for
 * a second-factor step that does not exist yet. Handling all three branches now
 * means turning two-factor on later is a server-side change alone.
 */
type SignInResponse =
  | { __typename: 'SignInSuccess'; user: AdminUser }
  | { __typename: 'SignInChallenge'; challengeId: string; method: string }
  | { __typename: 'SignInDenied'; reason: DeniedReason; retryAt: string | null };

export type DeniedReason =
  'INVALID_CREDENTIALS' | 'ACCOUNT_LOCKED' | 'ACCOUNT_DISABLED' | 'RECAPTCHA_FAILED';

/** What the sign-in page needs to render, once the network part is over. */
/** Why a passkey attempt did not produce a session. */
export type PasskeyFailure =
  /** The browser has no key for this site, or the person dismissed the prompt. */
  | { kind: 'no-passkey' }
  /** WebAuthn is not available at all — an old browser, or an insecure origin. */
  | { kind: 'unsupported' }
  | { kind: 'rejected' }
  | { kind: 'rate-limited'; retryAfterSeconds?: number }
  | { kind: 'network' };

export type SignInFailure =
  | { kind: 'denied'; reason: DeniedReason; retryAt: Date | null }
  | { kind: 'rate-limited'; retryAfterSeconds?: number }
  /** The password was right; a code from the authenticator app comes next. */
  | { kind: 'challenge'; challengeId: string }
  /** The server refused the input itself — a malformed address, or a password
   * longer than it will hash. */
  | { kind: 'rejected-input' }
  | { kind: 'network' };

const VERIFY_TWO_FACTOR = gql`
  mutation VerifyTwoFactor($input: VerifyTwoFactorInput!) {
    verifyTwoFactor(input: $input) {
      __typename
      ... on SignInSuccess {
        user {
          id
          email
          name
          role
          avatarUrl
        }
      }
      ... on SignInDenied {
        reason
        retryAt
      }
    }
  }
`;

const ME = gql`
  query Me {
    me {
      id
      email
      name
      role
      avatarUrl
    }
  }
`;

const SIGN_IN = gql`
  mutation SignIn($input: SignInInput!, $recaptchaToken: String) {
    signIn(input: $input, recaptchaToken: $recaptchaToken) {
      __typename
      ... on SignInSuccess {
        user {
          id
          email
          name
          role
          avatarUrl
        }
      }
      ... on SignInChallenge {
        challengeId
        method
      }
      ... on SignInDenied {
        reason
        retryAt
      }
    }
  }
`;

const BEGIN_PASSKEY_SIGN_IN = gql`
  mutation BeginPasskeySignIn($input: BeginPasskeySignInInput!) {
    beginPasskeySignIn(input: $input) {
      challengeId
      options
    }
  }
`;

const COMPLETE_PASSKEY_SIGN_IN = gql`
  mutation CompletePasskeySignIn($input: CompletePasskeyInput!) {
    completePasskeySignIn(input: $input) {
      __typename
      ... on SignInSuccess {
        user {
          id
          email
          name
          role
          avatarUrl
        }
      }
      ... on SignInDenied {
        reason
        retryAt
      }
    }
  }
`;

const REFRESH_SESSION = gql`
  mutation RefreshSession {
    refreshSession
  }
`;

/**
 * Shortest gap between two refreshes.
 *
 * Rotation is worth doing regularly and worth nothing done constantly: each one
 * costs a write and leaves a spent row behind, so a tab switched to and from
 * repeatedly would churn the sessions table for no gain.
 */
const REFRESH_EVERY_MS = 15 * 60_000;

/*
 * The last refresh time is stored rather than kept in a ref, because a ref is a
 * new zero on every page load — so reloading rotated the session every time,
 * and holding down F5 grew the sessions table without bound. Rows only leave it
 * when they expire, seven days later.
 *
 * localStorage rather than sessionStorage, which was the first choice and the
 * wrong one. sessionStorage is per *tab*, but the thing being throttled is rows
 * written to the server's sessions table, and that is per browser: opening the
 * admin in a second tab found an empty store and rotated again a minute after
 * the first one had. It fixed "hold down F5" and left "open ten tabs" alone.
 */
const LAST_REFRESH_KEY = 'sonasi.admin.last-session-refresh';

/*
 * The fallback for when storage is unavailable, and not an optimisation.
 *
 * Two ways to get this wrong, and the first version had both. Returning 0 from
 * a failed read never throttles, and the effect below refreshes on mount *and*
 * on every focus event — so a private window would rotate on every alt-tab,
 * worse than the per-page-load behaviour it replaced rather than equal to it.
 * And the read has to consider it even when storage itself is readable: the
 * common asymmetric case is Safari in private mode and any browser at its
 * quota, where `getItem` returns null and `setItem` throws. Returning only the
 * stored value there means the fallback is written where nothing reads it, and
 * the throttle silently never holds.
 *
 * So: read the later of the two.
 */
let lastRefreshFallback = 0;

const lastRefreshAt = (): number => {
  let stored = 0;
  try {
    stored = Number(localStorage.getItem(LAST_REFRESH_KEY)) || 0;
  } catch {
    /* Storage unavailable; the module-scope value below is the whole answer. */
  }
  return Math.max(stored, lastRefreshFallback);
};

const rememberRefresh = (): void => {
  try {
    localStorage.setItem(LAST_REFRESH_KEY, String(Date.now()));
  } catch {
    // Private mode or quota. The read above takes the later of the two values,
    // so this alone is enough — writing it unconditionally as well was a line
    // no test could tell apart from its absence.
    lastRefreshFallback = Date.now();
  }
};

const SIGN_OUT = gql`
  mutation SignOut {
    signOut
  }
`;

const REQUEST_PASSWORD_RESET = gql`
  mutation RequestPasswordReset($input: RequestPasswordResetInput!) {
    requestPasswordReset(input: $input)
  }
`;

const RESET_PASSWORD = gql`
  mutation ResetPassword($input: ResetPasswordInput!) {
    resetPassword(input: $input)
  }
`;

/**
 * Asks for a reset email. Resolves either way, on purpose.
 *
 * The server answers the same whether or not the address has an account, and
 * the screen must not undo that by behaving differently — so even a network
 * failure lands on the same confirmation. Outside the provider because nothing
 * about it needs a session.
 */
export const requestPasswordReset = async (email: string): Promise<void> => {
  try {
    await gqlClient.request(REQUEST_PASSWORD_RESET, { input: { email } });
  } catch {
    /* Deliberately ignored — see above. */
  }
};

export type ResetOutcome = 'done' | 'bad-link' | 'weak-password' | 'rate-limited' | 'network';

/** Redeems a reset link. */
export const resetPassword = async (token: string, newPassword: string): Promise<ResetOutcome> => {
  try {
    const { resetPassword: ok } = await gqlClient.request<{ resetPassword: boolean }>(
      RESET_PASSWORD,
      { input: { token, newPassword } },
    );
    // false means the link itself is no good — unknown, spent, or expired. The
    // server does not say which, and neither should this.
    return ok ? 'done' : 'bad-link';
  } catch (error) {
    const code = (error as ClientError)?.response?.errors?.[0]?.extensions?.code;
    if (code === 'RATE_LIMITED') return 'rate-limited';
    if (code === 'BAD_REQUEST' || code === 'BAD_USER_INPUT') return 'weak-password';
    return 'network';
  }
};

export const resetMessage = (outcome: Exclude<ResetOutcome, 'done'>): string => {
  switch (outcome) {
    case 'bad-link':
      return 'This link has expired or has already been used. Ask for a new one.';
    case 'weak-password':
      return 'Choose a password of at least 12 characters.';
    case 'rate-limited':
      return 'Too many attempts. Please wait a moment and try again.';
    default:
      return 'Could not reach the server. Check your connection and try again.';
  }
};

interface AuthValue {
  user: AdminUser | null;
  /** 'loading' only while the first `me` is in flight. */
  status: 'loading' | 'authenticated' | 'anonymous';
  signIn: (email: string, password: string) => Promise<SignInFailure | null>;
  /**
   * The code after the password. `null` is signed in; `'wrong'` is another
   * try on the same challenge; `'start-over'` means the challenge is dead —
   * expired, or its five tries spent — and the password step is next.
   */
  verifyTwoFactor: (
    challengeId: string,
    code: string,
    trustDevice: boolean,
  ) => Promise<'wrong' | 'start-over' | SignInFailure | null>;
  /** An address narrows the ceremony to that account's keys; null lets any passkey for this site answer. */
  signInWithPasskey: (email: string | null) => Promise<PasskeyFailure | null>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);
const ME_KEY = ['me'];

/**
 * Records who is signed in and throws away everything the last session cached.
 *
 * Clearing at all matters because the cache belongs to whoever was signed in
 * last. On a shared machine the next person would otherwise be shown the
 * previous person's pages straight from cache, before a single request goes out.
 *
 * `reset`, not `remove`, and not `queryClient.clear()`. Removing a query
 * destroys it, and any component still observing it is left holding the
 * destroyed one: it goes on rendering the previous session's data while the
 * cache behind it reads empty, with no refetch ever coming to correct it. A
 * component that happens to read this context re-renders and re-attaches, which
 * hides the problem for most pages and not for a page rendered beside the
 * button rather than under it. Resetting keeps the query object and empties it,
 * so there is nothing to be stranded on either way.
 *
 * `me` is set rather than reset because it is the one query whose new value is
 * already known — resetting it would empty it and send the screen back to
 * "still asking".
 *
 * The cost is a refetch: signing out under a mounted page reissues its query as
 * nobody. Accepted, because the alternative is a page that can show the last
 * person's data.
 */
const resetCacheFor = (queryClient: QueryClient, user: AdminUser | null): void => {
  queryClient.setQueryData(ME_KEY, user);
  void queryClient.resetQueries({ predicate: (query) => query.queryKey[0] !== ME_KEY[0] });
};
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isPending } = useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      const { me } = await gqlClient.request<{ me: AdminUser | null }>(ME);
      return me;
    },
    /*
     * The session lives in an httpOnly cookie, so the only way to know whether
     * one exists is to ask. Not retried: a failure here means "not signed in"
     * as far as the screen is concerned, and retrying only delays the login
     * form appearing.
     */
    retry: false,
    /*
     * Both of these override the client-wide defaults, on purpose. A session
     * can end without this tab hearing about it — it expires, another tab signs
     * out, or a password reset revokes it — and with the global settings
     * (staleTime Infinity, no refetch on focus) the screen would go on looking
     * signed in until a hard reload, with every action failing for no visible
     * reason. Coming back to the tab is the natural moment to re-ask.
     */
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });

  const signIn = useCallback(
    async (email: string, password: string): Promise<SignInFailure | null> => {
      let response: { signIn: SignInResponse };
      try {
        response = await gqlClient.request<{ signIn: SignInResponse }>(SIGN_IN, {
          input: { email, password },
          // Wired through already; stays null until reCAPTCHA keys are configured.
          recaptchaToken: null,
        });
      } catch (error) {
        const code = (error as ClientError)?.response?.errors?.[0]?.extensions?.code;
        if (code === 'RATE_LIMITED') {
          const seconds = (error as ClientError).response.errors?.[0]?.extensions
            ?.retryAfterSeconds as number | undefined;
          return { kind: 'rate-limited', retryAfterSeconds: seconds };
        }
        /*
         * The server rejected the shape of what was sent — an address that is
         * not one, or a password past the length the API will hash. Separated
         * from a network failure because the advice is opposite: one is fixed by
         * changing what was typed, the other by waiting. Telling someone to
         * check their connection when the real problem is a 300-character
         * password sends them to the wrong place entirely.
         */
        if (code === 'BAD_REQUEST' || code === 'BAD_USER_INPUT') {
          return { kind: 'rejected-input' };
        }
        return { kind: 'network' };
      }

      const result = response.signIn;
      if (result.__typename === 'SignInSuccess') {
        // Seeded rather than refetched: the mutation already returned the user,
        // and a second round trip is a visible pause before the dashboard.
        resetCacheFor(queryClient, result.user);
        return null;
      }
      if (result.__typename === 'SignInChallenge') {
        return { kind: 'challenge', challengeId: result.challengeId };
      }

      return {
        kind: 'denied',
        reason: result.reason,
        retryAt: result.retryAt ? new Date(result.retryAt) : null,
      };
    },
    [queryClient],
  );

  /**
   * Signs in with a passkey.
   *
   * Three steps that have to stay together: ask the server for a challenge,
   * let the browser sign it, hand the signature back. The middle step is the
   * browser's own prompt, so a person cancelling it is an ordinary outcome
   * rather than an error — which is why it is reported separately from a key
   * that was refused.
   */
  const signInWithPasskey = useCallback(
    async (email: string | null): Promise<PasskeyFailure | null> => {
      if (!browserSupportsWebAuthn()) return { kind: 'unsupported' };

      let ceremony: { beginPasskeySignIn: { challengeId: string; options: PublicKeyOptions } };
      try {
        ceremony = await gqlClient.request(BEGIN_PASSKEY_SIGN_IN, { input: { email } });
      } catch (error) {
        return fromTransport(error);
      }

      let credential: Record<string, unknown>;
      try {
        credential = (await startAuthentication({
          optionsJSON: ceremony.beginPasskeySignIn.options as never,
        })) as unknown as Record<string, unknown>;
      } catch {
        /*
         * Dismissing the prompt, having no key for this site, and a timeout all
         * arrive here and are indistinguishable from one another — the browser
         * deliberately does not say which, because saying would reveal whether
         * a key exists. So the message has to cover all three honestly.
         */
        return { kind: 'no-passkey' };
      }

      let response: { completePasskeySignIn: SignInResponse };
      try {
        response = await gqlClient.request(COMPLETE_PASSKEY_SIGN_IN, {
          input: { challengeId: ceremony.beginPasskeySignIn.challengeId, credential },
        });
      } catch (error) {
        return fromTransport(error);
      }

      const result = response.completePasskeySignIn;
      if (result.__typename !== 'SignInSuccess') return { kind: 'rejected' };

      resetCacheFor(queryClient, result.user);
      return null;
    },
    [queryClient],
  );

  /*
   * Rotates the session cookie in the background.
   *
   * The mutation exists so that a leaked cookie stops working the next time the
   * real owner is active — which only happens if something actually calls it.
   * Left uncalled it would be dead code with tests, which this repository has
   * produced three times already.
   *
   * Silent either way: this is housekeeping the person did not ask for, and a
   * failure leaves them holding a session that still works.
   */
  const refreshSession = useCallback(async () => {
    if (Date.now() - lastRefreshAt() < REFRESH_EVERY_MS) return;
    rememberRefresh();

    try {
      await gqlClient.request(REFRESH_SESSION);
    } catch {
      /* Ignored on purpose — see above. */
    }
  }, []);

  useEffect(() => {
    if (!data) return undefined;

    void refreshSession();
    // Coming back to the tab is the moment worth rotating on: it is when the
    // session is about to be used, and it costs nothing while nobody is here.
    const onFocus = () => void refreshSession();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [data, refreshSession]);

  const signOut = useCallback(async () => {
    try {
      await gqlClient.request(SIGN_OUT);
    } catch {
      /*
       * Swallowed, not merely finalised.
       *
       * `try/finally` alone runs the cleanup and then rethrows, so a sign-out
       * during an API restart still rejects — and the caller in AdminShell
       * chains `.then(() => navigate('/login'))` with no reject branch, so the
       * navigation silently never happens and the rejection reaches the console
       * unhandled. The screen only recovers because RequireAuth notices the
       * empty session and redirects on its own.
       *
       * Signing out is local as far as this promise is concerned: the server
       * failing to hear about it does not make the person less signed out here.
       */
    } finally {
      /*
       * Runs even if the request failed. Leaving the screen signed in after
       * someone pressed Sign out is the worse of the two outcomes — especially
       * on a shared machine, which is the case the button exists for.
       */
      resetCacheFor(queryClient, null);
    }
  }, [queryClient]);

  const verifyTwoFactor = useCallback(
    async (
      challengeId: string,
      code: string,
      trustDevice: boolean,
    ): Promise<'wrong' | 'start-over' | SignInFailure | null> => {
      let response: { verifyTwoFactor: SignInResponse };
      try {
        response = await gqlClient.request<{ verifyTwoFactor: SignInResponse }>(VERIFY_TWO_FACTOR, {
          input: { challengeId, code, trustDevice },
        });
      } catch (error) {
        const code = (error as ClientError)?.response?.errors?.[0]?.extensions?.code;
        if (code === 'RATE_LIMITED') {
          const seconds = (error as ClientError).response.errors?.[0]?.extensions
            ?.retryAfterSeconds as number | undefined;
          return { kind: 'rate-limited', retryAfterSeconds: seconds };
        }
        if (code === 'BAD_REQUEST' || code === 'BAD_USER_INPUT') return { kind: 'rejected-input' };
        return { kind: 'network' };
      }
      const result = response.verifyTwoFactor;
      if (result.__typename === 'SignInSuccess') {
        resetCacheFor(queryClient, result.user);
        return null;
      }
      if (result.__typename === 'SignInDenied') {
        /*
         * One reason for a wrong code and a dead challenge; the server tells
         * them apart with `retryAt`. Absent: try another code. Present: the
         * challenge is over, whether it expired or took its five tries.
         */
        return result.retryAt ? 'start-over' : 'wrong';
      }
      return 'start-over';
    },
    [queryClient],
  );

  const value = useMemo<AuthValue>(
    () => ({
      user: data ?? null,
      status: isPending ? 'loading' : data ? 'authenticated' : 'anonymous',
      signIn,
      verifyTwoFactor,
      signInWithPasskey,
      signOut,
    }),
    [data, isPending, signIn, verifyTwoFactor, signInWithPasskey, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** The sentence shown under the form for each way a sign-in can fail. */
export const signInMessage = (failure: SignInFailure): string => {
  switch (failure.kind) {
    case 'denied':
      switch (failure.reason) {
        case 'ACCOUNT_LOCKED':
          return failure.retryAt
            ? `Too many failed attempts. Try again after ${failure.retryAt.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}.`
            : 'Too many failed attempts. Try again shortly.';
        case 'ACCOUNT_DISABLED':
          return 'This account has been disabled. Ask an owner to restore it.';
        case 'RECAPTCHA_FAILED':
          return 'We could not verify this request. Please try again.';
        default:
          // One sentence for a wrong password and an unknown address alike:
          // saying which is wrong would confirm that the address has an account.
          return 'Email or password is incorrect.';
      }
    case 'rate-limited':
      return failure.retryAfterSeconds
        ? `Too many attempts. Try again in ${Math.ceil(failure.retryAfterSeconds / 60)} minute(s).`
        : 'Too many attempts. Please wait a moment and try again.';
    case 'challenge':
      // Never shown: the screen moves to the code step instead of printing this.
      return 'Enter the code from your authenticator app.';
    case 'rejected-input':
      return 'Check the email address and password — one of them is not in a form we accept.';
    default:
      return 'Could not reach the server. Check your connection and try again.';
  }
};

/** The server hands this through untouched; the browser helper parses it. */
type PublicKeyOptions = Record<string, unknown>;

/** Shared by both passkey steps: the two calls fail the same ways. */
/**
 * A transport failure, as one of the passkey outcomes.
 *
 * Exported because registering a key hits the same two walls signing in with
 * one does — the rate limiter and the network — and a second copy of this would
 * be a second place for the two to drift apart.
 */
export const fromTransport = (error: unknown): PasskeyFailure => {
  const errors = (error as ClientError)?.response?.errors;
  const code = errors?.[0]?.extensions?.code;
  if (code === 'RATE_LIMITED') {
    return {
      kind: 'rate-limited',
      retryAfterSeconds: errors?.[0]?.extensions?.retryAfterSeconds as number | undefined,
    };
  }
  return { kind: 'network' };
};

export const passkeyMessage = (failure: PasskeyFailure): string => {
  switch (failure.kind) {
    case 'unsupported':
      return 'This browser cannot use passkeys. Sign in with your password instead.';
    case 'no-passkey':
      /*
       * One sentence for three outcomes the browser refuses to distinguish:
       * no key for this site, a dismissed prompt, and a timeout. Guessing at
       * which would be wrong two times in three.
       */
      return 'No passkey was used. Register one first, or sign in with your password.';
    case 'rejected':
      return 'That passkey was not accepted. Sign in with your password instead.';
    case 'rate-limited':
      return failure.retryAfterSeconds
        ? `Too many attempts. Try again in ${Math.ceil(failure.retryAfterSeconds / 60)} minute(s).`
        : 'Too many attempts. Please wait a moment and try again.';
    default:
      return 'Could not reach the server. Check your connection and try again.';
  }
};
